import React, { FC, useState, useEffect } from 'react';
import {
  Box, Text, SideSheetContentCol, Spinner, SpinnerSize, Button, Icon, useSideSheetState, useToastsDispatch,
  ToastType,
  ToastAction
} from '@bechtel/gds';
import { DocumentList, DocumentViewer, formatBytes, useWeldAssignees, DeleteDocumentModal, DeleteDocumentError, useAppUser } from '@bechtel/client-common';
import { DocumentProps, WeldDetailItemDocumentsProps } from './types';
import { DocumentHeaderStyled, SideSheetContentColStyled, EmptyDocumentViewStyled } from './styled';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Attachment, CommodityWrapper, CommodityAssignee, DeleteCommodityAttachmentPayload, DeleteCommodityAttachmentInput, DeleteCommodityAttachmentError } from '@bechtel/types';
import { GET_WELD_ATTACHMENTS, DELETE_COMMODITY_ATTACHMENT } from '@bechtel/client-gql';
import { useQuery, useMutation, ApolloError } from '@apollo/client';
import { get } from 'lodash';
import { useWeldPermissions } from '~/hooks';
import { ReactComponent as ViewDetailsSvg } from '@bechtel/client-assets/dist/images/view-details.svg';

export const WeldDetailItemDocuments: FC<WeldDetailItemDocumentsProps> = ({ isoDocument, weld }) => {
  const { i18n } = useLingui();
  const { user } = useAppUser();
  const [selectedDocument, setSelectedDocument] = useState<DocumentProps | null>();
  const { id: commodityId, commodityAssignees } = weld;
  const assignedCrewMembers: CommodityAssignee[] = useWeldAssignees(commodityAssignees);
  const { canUploadDocument, canDeleteCommodityAttachment: canDelete } = useWeldPermissions(assignedCrewMembers);
  const [removeCommodityAttachmentError, setRemoveCommodityAttachmentError] = useState<
    DeleteCommodityAttachmentError | ApolloError | null
  >(null);
  const [deleted, setDeleted] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const dispatchToast = useToastsDispatch();

  const { loading, error, data, refetch } = useQuery<{ commoditiesByIds: CommodityWrapper[] }, { ids: string[] }>(
    GET_WELD_ATTACHMENTS,
    {
      variables: {
        ids: [commodityId],
      },
    }
  );
  const commodityAttachments: Attachment[] = get(data, 'commoditiesByIds[0].weld.attachments', []);

  const {
    close: closeDocumentManager,
  } = useSideSheetState(false);

  const [deleteCommodityAttachment, { loading: deleteCommodityAttachmentLoading }] = useMutation<
    { deleteCommodityAttachment: DeleteCommodityAttachmentPayload },
    { input: DeleteCommodityAttachmentInput }
  >(DELETE_COMMODITY_ATTACHMENT, {
    variables: {
      input: {
        attachmentId: selectedDocument?.documentId ? selectedDocument.documentId : '',
        commodityId
      },
    },
    onCompleted: (data: { deleteCommodityAttachment: DeleteCommodityAttachmentPayload }) => {
      const deleteCommodityAttachmentError = get(data, 'deleteCommodityAttachment.error', null);
      if (deleteCommodityAttachmentError) {
        setRemoveCommodityAttachmentError(removeCommodityAttachmentError);
      } else {
        const deleteDocumentToast: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.SUCCESS,
            content: t`Document Deleted`,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(deleteDocumentToast);
        setDeleteConfirmed(false);
        setSelectedDocument(null);
        setDeleted(true);

      }
      setRemoveCommodityAttachmentError(deleteCommodityAttachmentError);
    },
    onError: (error) => {
      setRemoveCommodityAttachmentError(error);
    },
  });

  const closeErrorModal = () => {
    setRemoveCommodityAttachmentError(null);
  };

  useEffect(() => {
    if (deleted && commodityAttachments?.length == 0) {
      closeDocumentManager();
    }
  }, [deleted, commodityAttachments]);

  return (
    <>
      {!loading && error ? (
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          flexDirection={{ sm: 'column' }}
          p={{ sm: '500' }}
        >
          <Box pb={{ sm: '500' }}>
            <Text>
              <Trans>There was a problem loading Documents. Please click the button below to try again.</Trans>
            </Text>
          </Box>
          <Button onClick={() => refetch()} css={{ width: 'auto' }}>
            <Box mr={{ sm: '400' }}>
              <Icon icon="undo" />
            </Box>
            <Trans>Retry</Trans>
          </Button>
        </Box>
      ) : (
        <Box>
          {loading ? (
            <Box
              p={{ sm: '500' }}
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexGrow={{ sm: 1 }}
              css={'height: 100%;'}
            >
              <Spinner size={SpinnerSize.LARGE} />
            </Box>
          ) : (
            <>
              <SideSheetContentColStyled style={{ overflowY: 'hidden' }}>
                <DocumentList
                  documents={commodityAttachments}
                  isoDocument={isoDocument}
                  canUploadDocument={canUploadDocument}
                  onSelect={(document) => {
                    setSelectedDocument({
                      url: document.url,
                      documentNumber: document.fileName,
                      documentId: document.id,
                      fileSize: document.fileSize,
                      isIsoDoc: false,
                      creator: document.creator,
                    });
                  }}
                  onIsoSelect={(isoDocument) => {
                    setSelectedDocument({
                      url: isoDocument.url,
                      documentNumber: isoDocument.documentNumber,
                      documentId: isoDocument.documentNumber,
                      docHeader: t`ISO`,
                      isIsoDoc: true,
                    });
                  }}
                  selectedDocumentId={selectedDocument?.documentId}
                  commodityId={commodityId}
                ></DocumentList>
              </SideSheetContentColStyled>
              <SideSheetContentCol style={{ overflowY: 'hidden' }}>
                {selectedDocument && (
                  <DocumentHeaderStyled>
                    {selectedDocument.docHeader && (
                      <Box mb={{ sm: '300' }}>
                        <Text weight="600" size="300" color="grey.700">
                          {i18n._(selectedDocument.docHeader)}
                        </Text>
                      </Box>
                    )}
                    <Text color="grey.700" size="300" weight={selectedDocument.docHeader ? '000' : '600'}>
                      {selectedDocument.documentNumber} &nbsp;
                      {selectedDocument.fileSize && `(${formatBytes(parseFloat(selectedDocument.fileSize))})`}
                    </Text>
                  </DocumentHeaderStyled>
                )}
                {selectedDocument ? (
                  <DocumentViewer
                    url={selectedDocument?.url}
                    deleting={deleteCommodityAttachmentLoading}
                    canDelete={canDelete && selectedDocument?.creator?.id === user?.id && !selectedDocument.isIsoDoc}
                    onDelete={() => {
                      setDeleteConfirmed(true);
                    }}
                  ></DocumentViewer>
                ) : (
                  <EmptyDocumentViewStyled>
                    <ViewDetailsSvg></ViewDetailsSvg>
                    <Box mt={{ sm: '700' }}>
                      <Text color="grey.600" size="400">
                        <Trans> Select a document to preview.</Trans>
                      </Text>
                    </Box>
                  </EmptyDocumentViewStyled>
                )}
                {deleteConfirmed && (
                  <DeleteDocumentModal
                    onDelete={() => { setDeleted(false); deleteCommodityAttachment(); }}
                    onCancel={() => setDeleteConfirmed(false)}
                    title={'Delete Document'}
                    textContent={`Are you sure you want to delete the document ${selectedDocument?.documentNumber} ?`}
                  />
                )}
                {removeCommodityAttachmentError && (
                  <DeleteDocumentError error={removeCommodityAttachmentError} onClose={closeErrorModal} />
                )}
              </SideSheetContentCol>
            </>
          )}
        </Box>
      )}
    </>
  );
};
